'use client'

import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Code,
  Link as LinkIcon,
  Unlink,
  ImagePlus,
  Minus,
  Undo,
  Redo,
  Check,
  X,
} from 'lucide-react'
import { clsx } from 'clsx'
import { safeImageUrl } from '@/lib/utils/image-url'

interface Tool {
  icon: typeof Bold
  label: string
  /** Omitted for `upload`, whose click has to read a ref — see the note below. */
  action?: () => void
  /** Opens the hidden file input rather than running an editor command. */
  upload?: true
  active?: boolean
  disabled?: boolean
}

type Size = 'compact' | 'article'

/**
 * The two looks, in one place. `compact` is the original course-form box;
 * `article` is the writing surface: reading-measure type, no box, the toolbar
 * detached above the text and sticky below the editor's own top bar.
 *
 * The sticky offsets are the app's chrome: the black TopBar is `sticky` at
 * 60px below `lg` and static above it; the editor bar is 56px (`h-14`). A
 * toolbar at `top-0` sat under both, which hid the headline deliverable of the
 * rebuild as soon as the writer scrolled past the first paragraph.
 */
const VARIANT: Record<
  Size,
  {
    editor: string
    lineHeight: number
    wrapper: string
    toolbarWrap?: string
    toolbar: string
    button: string
    icon: string
    content?: string
  }
> = {
  compact: {
    editor:
      'prose prose-sm max-w-none focus:outline-none px-3 py-2.5 text-[13px] text-text-primary leading-relaxed',
    lineHeight: 1.5,
    wrapper:
      'border border-border-default rounded-lg overflow-hidden focus-within:border-border-hover focus-within:ring-1 focus-within:ring-border-hover/40',
    toolbar: 'gap-0.5 px-2 py-1.5 bg-bg-subtle border-b border-border-default',
    button: 'p-1.5',
    icon: 'w-3.5 h-3.5',
  },
  article: {
    editor: 'max-w-none focus:outline-none text-[17px] text-text-primary leading-[1.7]',
    lineHeight: 1.7,
    wrapper: 'relative',
    toolbarWrap: 'sticky top-[116px] lg:top-14 z-10 bg-bg-page/95 backdrop-blur',
    toolbar: 'gap-1 py-2 border-b border-border-default',
    button: 'p-2',
    icon: 'w-4 h-4',
    content: 'pt-6',
  },
}

interface RichTextareaProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  className?: string
  size?: Size
  /**
   * Enables the image button, drag-and-drop and paste-to-upload. Given a file,
   * returns the public URL to insert. Omitted, the image affordances are hidden
   * entirely rather than shown broken — the previous editor's dead image button
   * is the defect this component is replacing.
   */
  onUploadImage?: (file: File) => Promise<string>
  /** Announced on the editor region. */
  ariaLabel?: string
}

/**
 * The house rich-text editor.
 *
 * Used by the course lesson forms (`compact`) and the article editor
 * (`article`). TipTap is configured to `levels: [2, 3]` because the page
 * renders the title as the document's only h1, and `renderSafeHtml` strips a
 * body h1 for the same reason.
 */
export function RichTextarea({
  value,
  onChange,
  placeholder,
  rows = 12,
  className,
  size = 'compact',
  onUploadImage,
  ariaLabel,
}: RichTextareaProps) {
  const v = VARIANT[size]
  const fileRef = useRef<HTMLInputElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  // The alt-text prompt. `altTarget` is the `src` of the image it describes.
  const [altTarget, setAltTarget] = useState<string | null>(null)
  const [altValue, setAltValue] = useState('')

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-brand-text underline' },
      }),
      Image,
      Placeholder.configure({
        placeholder: placeholder || 'Start writing…',
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: v.editor,
        style: `min-height: ${rows * v.lineHeight}em`,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
      // Pasted HTML (a Google Doc, a web page) can carry `<img>` tags pointing
      // anywhere. The sanitiser removes those at render, so the author would
      // see the image in the editor and readers would not. Strip them at the
      // paste instead, so what the author sees is what will ship.
      transformPastedHTML(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        doc.querySelectorAll('img').forEach((img) => {
          if (!safeImageUrl(img.getAttribute('src'))) img.remove()
        })
        return doc.body.innerHTML
      },
      // Clicking an image reopens its description.
      handleClickOn(_view, _pos, node) {
        if (node.type.name === 'image') {
          setAltTarget(node.attrs.src as string)
          setAltValue((node.attrs.alt as string) ?? '')
        }
        return false
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  // Sync external value changes (e.g. switching between lessons, or a draft
  // arriving from the server after first paint).
  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [value, editor])

  const insertImage = useCallback(
    async (file: File) => {
      if (!onUploadImage || !editor) return
      setUploadError(null)
      setUploading(true)
      try {
        const url = await onUploadImage(file)
        editor.chain().focus().setImage({ src: url, alt: '' }).run()
        // Ask for the description straight away, while the author still knows
        // what the picture is for. Skippable — "It's decorative" is one click.
        setAltValue('')
        setAltTarget(url)
      } catch (err) {
        // The author has just waited for an upload. Telling them nothing — the
        // old behaviour of the dead image button — is the worst option.
        setUploadError(err instanceof Error ? err.message : 'That image could not be uploaded.')
      } finally {
        setUploading(false)
      }
    },
    // The two setters are stable, but the compiler cannot prove it once they
    // are also captured by the `useEditor` config above, so they are listed.
    [editor, onUploadImage, setAltValue, setAltTarget],
  )

  /** Write `alt` onto the image node whose `src` is `altTarget`. */
  function applyAlt(alt: string) {
    if (!editor || !altTarget) return
    let at: number | null = null
    editor.state.doc.descendants((node, pos) => {
      if (at === null && node.type.name === 'image' && node.attrs.src === altTarget) at = pos
      return at === null
    })
    if (at !== null) {
      editor
        .chain()
        .setNodeSelection(at)
        .updateAttributes('image', { alt: alt.trim() })
        .focus()
        .run()
    }
    setAltTarget(null)
    setAltValue('')
  }

  // Paste and drop. Both are how people actually get a screenshot into a piece
  // of writing, and neither existed before.
  useEffect(() => {
    if (!editor || !onUploadImage) return
    const dom = editor.view.dom

    const filesFrom = (list: FileList | null | undefined) =>
      Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))

    const onPaste = (e: ClipboardEvent) => {
      const files = filesFrom(e.clipboardData?.files)
      if (files.length === 0) return
      e.preventDefault()
      files.forEach(insertImage)
    }
    const onDrop = (e: DragEvent) => {
      const files = filesFrom(e.dataTransfer?.files)
      if (files.length === 0) return
      e.preventDefault()
      files.forEach(insertImage)
    }
    const onDragOver = (e: DragEvent) => {
      if (filesFrom(e.dataTransfer?.files).length > 0) e.preventDefault()
    }

    dom.addEventListener('paste', onPaste)
    dom.addEventListener('drop', onDrop)
    dom.addEventListener('dragover', onDragOver)
    return () => {
      dom.removeEventListener('paste', onPaste)
      dom.removeEventListener('drop', onDrop)
      dom.removeEventListener('dragover', onDragOver)
    }
  }, [editor, onUploadImage, insertImage])

  function openLinkPrompt() {
    if (!editor) return
    setLinkValue(editor.getAttributes('link').href ?? '')
    setLinkOpen(true)
  }

  function applyLink() {
    if (!editor) return
    const href = linkValue.trim()
    if (!href) {
      editor.chain().focus().unsetLink().run()
    } else {
      // A bare domain is what people type. Without a scheme the browser reads
      // it as a relative path and the link 404s inside the app.
      const withScheme = /^(https?:|mailto:)/i.test(href) ? href : `https://${href}`
      editor.chain().focus().extendMarkRange('link').setLink({ href: withScheme }).run()
    }
    setLinkOpen(false)
    setLinkValue('')
  }

  if (!editor) return null

  // Grouped with dividers: text style · emphasis · blocks · insert · history.
  // The old flat row of twelve identical squares gave a keyboard user twelve
  // indistinguishable stops and a sighted user no structure at all.
  const groups: Tool[][] = [
    [
      {
        icon: Heading2,
        label: 'Heading',
        action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        active: editor.isActive('heading', { level: 2 }),
      },
      {
        icon: Heading3,
        label: 'Subheading',
        action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
        active: editor.isActive('heading', { level: 3 }),
      },
    ],
    [
      {
        icon: Bold,
        label: 'Bold',
        action: () => editor.chain().focus().toggleBold().run(),
        active: editor.isActive('bold'),
      },
      {
        icon: Italic,
        label: 'Italic',
        action: () => editor.chain().focus().toggleItalic().run(),
        active: editor.isActive('italic'),
      },
      {
        icon: Code,
        label: 'Code',
        action: () => editor.chain().focus().toggleCode().run(),
        active: editor.isActive('code'),
      },
    ],
    [
      {
        icon: List,
        label: 'Bullet list',
        action: () => editor.chain().focus().toggleBulletList().run(),
        active: editor.isActive('bulletList'),
      },
      {
        icon: ListOrdered,
        label: 'Numbered list',
        action: () => editor.chain().focus().toggleOrderedList().run(),
        active: editor.isActive('orderedList'),
      },
      {
        icon: Quote,
        label: 'Quote',
        action: () => editor.chain().focus().toggleBlockquote().run(),
        active: editor.isActive('blockquote'),
      },
    ],
    [
      {
        icon: editor.isActive('link') ? Unlink : LinkIcon,
        label: editor.isActive('link') ? 'Edit or remove link' : 'Add link',
        action: openLinkPrompt,
        active: editor.isActive('link'),
      },
      ...(onUploadImage
        ? [
            {
              icon: ImagePlus,
              label: uploading ? 'Uploading image…' : 'Insert image',
              // ⚠️ No `action` here, deliberately. `groups` is built during
              // render, and a closure in it that reads `fileRef.current`
              // TAINTS THE WHOLE ARRAY for the React compiler — it cannot prove
              // the closure is never called during render, so every later use
              // of `groups` fails the "Cannot access refs during render" rule.
              // The click opens the picker in the JSX handler instead, where
              // reading a ref is exactly what handlers are for.
              upload: true as const,
              disabled: uploading,
            },
          ]
        : []),
      {
        icon: Minus,
        label: 'Divider',
        action: () => editor.chain().focus().setHorizontalRule().run(),
      },
    ],
    [
      {
        icon: Undo,
        label: 'Undo',
        action: () => editor.chain().focus().undo().run(),
        disabled: !editor.can().undo(),
      },
      {
        icon: Redo,
        label: 'Redo',
        action: () => editor.chain().focus().redo().run(),
        disabled: !editor.can().redo(),
      },
    ],
  ]

  const flat = groups.flat()

  /**
   * Roving tabindex. A `role="toolbar"` is ONE tab stop; the arrow keys move
   * within it. Without this, tabbing from the title to the body crosses twelve
   * buttons, which is the accessibility floor this rebuild had to clear.
   */
  function onToolbarKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    const buttons = Array.from(
      toolbarRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [],
    )
    if (buttons.length === 0) return
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement)
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? buttons.length - 1
          : e.key === 'ArrowRight'
            ? (i + 1 + buttons.length) % buttons.length
            : (i - 1 + buttons.length) % buttons.length
    buttons[next]?.focus()
  }

  const firstEnabled = flat.find((t) => !t.disabled)

  const toolbar = (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label="Formatting"
      aria-orientation="horizontal"
      onKeyDown={onToolbarKeyDown}
      className={clsx('flex items-center flex-wrap', v.toolbar)}
    >
      {groups.map((group, gi) => (
        <div key={gi} className="flex items-center gap-0.5">
          {gi > 0 && <div className="w-px h-5 bg-border-default mx-1.5" aria-hidden />}
          {group.map((tool) => (
            <button
              key={tool.label}
              type="button"
              onClick={() => (tool.upload ? fileRef.current?.click() : tool.action?.())}
              title={tool.label}
              aria-label={tool.label}
              aria-pressed={tool.active ?? undefined}
              disabled={tool.disabled}
              tabIndex={tool === firstEnabled ? 0 : -1}
              className={clsx(
                'rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-border-hover',
                v.button,
                tool.disabled && 'opacity-40 cursor-not-allowed',
                !tool.disabled && tool.active
                  ? 'bg-brand-dark text-white'
                  : !tool.disabled && 'text-text-muted hover:bg-bg-subtle hover:text-text-primary',
              )}
            >
              <tool.icon className={v.icon} aria-hidden />
            </button>
          ))}
        </div>
      ))}
    </div>
  )

  return (
    <div className={clsx(v.wrapper, className)}>
      <div className={v.toolbarWrap}>{toolbar}</div>

      {linkOpen && (
        <LinkPopover
          value={linkValue}
          onChange={setLinkValue}
          onApply={applyLink}
          onCancel={() => setLinkOpen(false)}
          editor={editor}
        />
      )}

      {altTarget && (
        <AltPopover
          value={altValue}
          onChange={setAltValue}
          onApply={() => applyAlt(altValue)}
          onDecorative={() => applyAlt('')}
          onCancel={() => setAltTarget(null)}
        />
      )}

      {uploadError && (
        <p role="alert" className="text-sm text-danger mt-2">
          {uploadError}
        </p>
      )}
      {uploading && (
        <p className="text-sm text-text-muted mt-2" aria-live="polite">
          Uploading image…
        </p>
      )}

      {onUploadImage && (
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) insertImage(file)
            e.target.value = ''
          }}
        />
      )}

      <EditorContent editor={editor} className={v.content} />
    </div>
  )
}

/**
 * The link prompt, inline.
 *
 * It replaces `window.prompt`, which cannot be styled, cannot be cancelled with
 * a click outside, is announced by screen readers as a browser dialog with no
 * relationship to the editor, and is suppressed outright by some browsers in
 * cross-origin frames.
 */
function LinkPopover({
  value,
  onChange,
  onApply,
  onCancel,
  editor,
}: {
  value: string
  onChange: (v: string) => void
  onApply: () => void
  onCancel: () => void
  editor: Editor
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  return (
    <div className="flex items-center gap-2 mt-2 p-2 bg-white border border-border-default rounded-lg shadow-sm">
      <label htmlFor="rt-link" className="sr-only">
        Link address
      </label>
      <input
        id="rt-link"
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onApply()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        placeholder="example.com/page"
        className="flex-1 px-2.5 py-1.5 bg-bg-page border border-border-default rounded-md text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-hover"
      />
      <button
        type="button"
        onClick={onApply}
        aria-label="Apply link"
        className="p-1.5 rounded-md text-text-secondary hover:bg-bg-subtle hover:text-text-primary"
      >
        <Check className="w-4 h-4" aria-hidden />
      </button>
      {editor.isActive('link') && (
        <button
          type="button"
          onClick={() => {
            editor.chain().focus().unsetLink().run()
            onCancel()
          }}
          className="text-xs font-medium text-text-muted hover:text-danger px-1.5"
        >
          Remove
        </button>
      )}
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancel"
        className="p-1.5 rounded-md text-text-muted hover:bg-bg-subtle hover:text-text-primary"
      >
        <X className="w-4 h-4" aria-hidden />
      </button>
    </div>
  )
}

/**
 * The image description prompt.
 *
 * Without it every inline image is permanently `alt=""` — invisible to a
 * screen reader — and a CRM how-to is mostly screenshots. Opens after each
 * upload and on clicking an image; one click marks a picture decorative.
 */
function AltPopover({
  value,
  onChange,
  onApply,
  onDecorative,
  onCancel,
}: {
  value: string
  onChange: (v: string) => void
  onApply: () => void
  onDecorative: () => void
  onCancel: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 p-2 bg-white border border-border-default rounded-lg shadow-sm">
      <label htmlFor="rt-alt" className="sr-only">
        Image description
      </label>
      <input
        id="rt-alt"
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onApply()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        placeholder="Describe this image for people who can't see it"
        className="flex-1 min-w-[12rem] px-2.5 py-1.5 bg-bg-page border border-border-default rounded-md text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-hover"
      />
      <button
        type="button"
        onClick={onApply}
        aria-label="Save description"
        className="p-1.5 rounded-md text-text-secondary hover:bg-bg-subtle hover:text-text-primary"
      >
        <Check className="w-4 h-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onDecorative}
        className="text-xs font-medium text-text-muted hover:text-text-primary px-1.5"
      >
        It&apos;s decorative
      </button>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancel"
        className="p-1.5 rounded-md text-text-muted hover:bg-bg-subtle hover:text-text-primary"
      >
        <X className="w-4 h-4" aria-hidden />
      </button>
    </div>
  )
}
